import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Input, Button, Spin, message, Empty } from 'antd';
import {
	fetchS3Bytes,
	scanPdfFields,
	generateFromFieldOverrides,
} from 'src/certificate-generator/certificatePdfUtils';

const AUTO_FILLED_VARIABLES = new Set([
	'recipientName',
	'onFullDate',
	'monthYear',
]);

function getAutoFilledValues(winner) {
	const now = new Date();
	const recipientName = winner?.nominee
		? `${winner.nominee.firstName || ''} ${winner.nominee.lastName || ''}`.trim()
		: '';
	const onFullDate = now.toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	});
	const monthYear = now.toLocaleDateString('en-US', {
		month: 'long',
		year: 'numeric',
	});
	return { recipientName, onFullDate, monthYear };
}

const CertificateFieldsModal = ({
	visible,
	onCancel,
	onSubmit,
	winners,
	certificateConfig,
}) => {
	const [loading, setLoading] = useState(true);
	const [configData, setConfigData] = useState(null);
	const [pdfBytes, setPdfBytes] = useState(null);
	const [scannedFields, setScannedFields] = useState([]);
	const [currentWinnerIndex, setCurrentWinnerIndex] = useState(0);
	// Map<nomineeId, Record<variableName, value>>
	const [fieldValuesMap, setFieldValuesMap] = useState(new Map());
	const [previewUrl, setPreviewUrl] = useState(null);
	const [previewing, setPreviewing] = useState(false);
	const previewUrlRef = useRef(null);
	const debounceRef = useRef(null);

	const customFields = (configData?.fields || []).filter(
		(f) => !AUTO_FILLED_VARIABLES.has(f.variableName)
	);

	const currentWinner = winners[currentWinnerIndex];
	const currentNomineeId = currentWinner?.nomineeId;
	const currentValues = fieldValuesMap.get(currentNomineeId) || {};
	const currentValuesKey = JSON.stringify(currentValues);

	const revokePreview = useCallback(() => {
		if (previewUrlRef.current) {
			URL.revokeObjectURL(previewUrlRef.current);
			previewUrlRef.current = null;
		}
	}, []);

	// Load certificate config and template on mount
	useEffect(() => {
		if (!certificateConfig?.key) return;

		let cancelled = false;
		const loadConfig = async () => {
			setLoading(true);
			try {
				const configBytes = await fetchS3Bytes(certificateConfig.key);
				const config = JSON.parse(new TextDecoder().decode(configBytes));
				if (cancelled) return;
				setConfigData(config);

				const templateBytes = await fetchS3Bytes(config.templateKey);
				if (cancelled) return;
				setPdfBytes(templateBytes);

				const fields = await scanPdfFields(templateBytes);
				if (cancelled) return;
				setScannedFields(fields);
			} catch (error) {
				console.error('Failed to load certificate config:', error);
				if (!cancelled) {
					message.error('Failed to load certificate template.');
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		loadConfig();
		return () => {
			cancelled = true;
		};
	}, [certificateConfig?.key]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			revokePreview();
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [revokePreview]);

	// Build overrides object from current values for preview
	const buildOverrides = useCallback(
		(values) => {
			if (!configData?.fields) return {};
			const autoValues = getAutoFilledValues(currentWinner);
			const overrides = {};
			for (const field of configData.fields) {
				if (AUTO_FILLED_VARIABLES.has(field.variableName)) {
					overrides[field.fieldName] = autoValues[field.variableName];
				} else if (values[field.variableName]) {
					overrides[field.fieldName] = values[field.variableName];
				}
			}

			return overrides;
		},
		[configData, currentWinner]
	);

	// Build field styles from config
	const buildFieldStyles = useCallback(() => {
		if (!configData?.fields) return {};
		const styles = {};
		for (const field of configData.fields) {
			if (field.style) {
				const style = {};
				if (field.style.fontSize != null) style.fontSize = field.style.fontSize;
				if (field.style.color) style.colorHex = field.style.color;
				if (field.style.offsetX != null) style.offsetX = field.style.offsetX;
				if (field.style.offsetY != null) style.offsetY = field.style.offsetY;
				if (field.style.centerHorizontally) style.centerHorizontally = true;
				if (Object.keys(style).length > 0) {
					styles[field.fieldName] = style;
				}
			}
		}

		return styles;
	}, [configData]);

	// Update preview
	const updatePreview = useCallback(
		async (values) => {
			if (!pdfBytes || !configData) return;
			setPreviewing(true);
			try {
				const overrides = buildOverrides(values);
				const fieldStyles = buildFieldStyles();
				const modifiedPdf = await generateFromFieldOverrides(
					pdfBytes,
					overrides,
					scannedFields,
					fieldStyles
				);
				revokePreview();
				const blob = new Blob([modifiedPdf], { type: 'application/pdf' });
				const url = URL.createObjectURL(blob);
				previewUrlRef.current = url;
				setPreviewUrl(url);
			} catch (error) {
				console.error('Preview failed:', error);
			} finally {
				setPreviewing(false);
			}
		},
		[
			pdfBytes,
			configData,
			scannedFields,
			buildOverrides,
			buildFieldStyles,
			revokePreview,
		]
	);

	// Debounced preview update when values or winner change
	useEffect(() => {
		if (!pdfBytes || !configData) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			updatePreview(currentValues);
		}, 400);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [
		currentValuesKey,
		currentWinnerIndex,
		pdfBytes,
		configData,
		updatePreview,
	]);

	const handleFieldChange = (variableName, value) => {
		setFieldValuesMap((prev) => {
			const next = new Map(prev);
			const existing = next.get(currentNomineeId) || {};
			next.set(currentNomineeId, { ...existing, [variableName]: value });
			return next;
		});
	};

	const handleSubmit = () => {
		// Build the final map: Map<nomineeId, Array<{key, value}>>
		const resultMap = new Map();
		for (const winner of winners) {
			const nomineeId = winner.nomineeId;
			const customValues = fieldValuesMap.get(nomineeId) || {};
			const autoValues = getAutoFilledValues(winner);
			const fieldValues = [];

			for (const field of configData.fields) {
				if (AUTO_FILLED_VARIABLES.has(field.variableName)) {
					fieldValues.push({
						key: field.variableName,
						value: autoValues[field.variableName],
					});
				} else if (customValues[field.variableName]) {
					fieldValues.push({
						key: field.variableName,
						value: customValues[field.variableName],
					});
				}
			}

			resultMap.set(nomineeId, fieldValues);
		}

		onSubmit(resultMap);
	};

	const winnerName = currentWinner?.nominee
		? `${currentWinner.nominee.firstName || ''} ${currentWinner.nominee.lastName || ''}`.trim()
		: `Winner ${currentWinnerIndex + 1}`;

	const isLastWinner = currentWinnerIndex === winners.length - 1;

	return (
		<Modal
			open={visible}
			onCancel={onCancel}
			title="Certificate Field Values"
			width={950}
			footer={null}
			destroyOnClose
		>
			{loading ? (
				<div style={{ textAlign: 'center', padding: 48 }}>
					<Spin tip="Loading certificate template..." />
				</div>
			) : !configData || !pdfBytes ? (
				<Empty description="Failed to load certificate template" />
			) : (
				<div style={{ display: 'flex', gap: 16 }}>
					{/* PDF Preview */}
					<div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
						{previewing && (
							<div
								style={{
									position: 'absolute',
									top: 8,
									right: 8,
									zIndex: 1,
								}}
							>
								<Spin size="small" />
							</div>
						)}
						{previewUrl ? (
							<iframe
								src={previewUrl}
								style={{
									width: '100%',
									height: 550,
									border: '1px solid #d9d9d9',
									borderRadius: 6,
								}}
								title="Certificate Preview"
							/>
						) : (
							<div
								style={{
									height: 550,
									border: '1px solid #d9d9d9',
									borderRadius: 6,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<Spin tip="Generating preview..." />
							</div>
						)}
					</div>

					{/* Field Inputs */}
					<div style={{ width: 300, flexShrink: 0 }}>
						<h4 style={{ marginBottom: 16 }}>
							Winner {currentWinnerIndex + 1} of {winners.length}: {winnerName}
						</h4>

						{customFields.length === 0 ? (
							<p style={{ color: '#595959' }}>
								No custom fields to fill in. All fields are auto-populated.
							</p>
						) : (
							customFields.map((field) => (
								<div key={field.variableName} style={{ marginBottom: 12 }}>
									<label
										style={{
											display: 'block',
											marginBottom: 4,
											fontWeight: 500,
										}}
									>
										{field.variableName}
									</label>
									<Input
										value={currentValues[field.variableName] || ''}
										onChange={(e) =>
											handleFieldChange(field.variableName, e.target.value)
										}
										placeholder={`Enter ${field.variableName}`}
									/>
								</div>
							))
						)}

						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								marginTop: 24,
							}}
						>
							<Button
								disabled={currentWinnerIndex === 0}
								onClick={() => setCurrentWinnerIndex((i) => i - 1)}
							>
								Previous
							</Button>
							{isLastWinner ? (
								<Button type="primary" onClick={handleSubmit}>
									Submit
								</Button>
							) : (
								<Button
									type="primary"
									onClick={() => setCurrentWinnerIndex((i) => i + 1)}
								>
									Next
								</Button>
							)}
						</div>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default CertificateFieldsModal;
